import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileSpreadsheet, Loader2, Download } from "lucide-react";

const LS_KEY = "dnf-peticiones-export-v1";
const leer = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; } };

function ColumnasHoja({ titulo, activa, onToggleHoja, defs, presets, sel, onChange, testid }) {
  const preset = (keys) => onChange(keys);
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${activa ? "border-amber-500/40 bg-zinc-900/60" : "border-zinc-800 bg-zinc-900/20 opacity-70"}`} data-testid={`${testid}-bloque`}>
      <label className="flex items-center gap-2 text-sm font-semibold text-zinc-100 cursor-pointer">
        <input type="checkbox" checked={activa} onChange={(e) => onToggleHoja(e.target.checked)} data-testid={`${testid}-hoja`} className="accent-amber-500" /> Hoja «{titulo}»
        <span className="ml-auto text-[10px] text-zinc-500">{sel.length}/{defs.length} columnas</span>
      </label>
      {activa && (
        <>
          <div className="flex flex-wrap gap-1">
            {Object.keys(presets).map((p) => (
              <button key={p} type="button" onClick={() => preset(presets[p])} data-testid={`${testid}-preset-${p}`}
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-zinc-700 text-zinc-300 hover:border-amber-500/60 hover:text-amber-300">{p}</button>
            ))}
            <button type="button" onClick={() => preset([])} className="text-[10px] uppercase px-2 py-0.5 rounded border border-zinc-800 text-zinc-500 hover:text-zinc-200">ninguna</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 max-h-40 overflow-y-auto pr-1">
            {defs.map((c) => (
              <label key={c.key} className="flex items-center gap-1.5 text-xs text-zinc-300 cursor-pointer">
                <input type="checkbox" checked={sel.includes(c.key)} data-testid={`${testid}-col-${c.key}`} className="accent-amber-500"
                  onChange={(e) => onChange(e.target.checked ? [...sel, c.key] : sel.filter((k) => k !== c.key))} /> {c.label}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Diálogo Exportar Excel. `idsFiltradas`/`idsSeleccionadas` ya vienen en el orden exacto del listado. */
export default function ExportPeticionesDialog({ open, onClose, idsFiltradas, idsSeleccionadas, totalCargadas, filtrosDescripcion }) {
  const [meta, setMeta] = useState(null);
  const guardado = useMemo(leer, [open]);
  const [alcance, setAlcance] = useState("filtradas");
  const [hojas, setHojas] = useState(guardado.hojas || { peticiones: true, lineas: true, componentes: false });
  const [colsP, setColsP] = useState(guardado.colsP || null);
  const [colsL, setColsL] = useState(guardado.colsL || null);
  const [colsC, setColsC] = useState(guardado.colsC || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAlcance(idsSeleccionadas.length > 0 ? "seleccionadas" : "filtradas");
    api.get("/peticiones/export/columnas").then(({ data }) => {
      setMeta(data);
      setColsP((v) => v || data.presets.peticiones.operativo);
      setColsL((v) => v || data.presets.lineas.conciliacion);
      setColsC((v) => v || data.presets.componentes.logistica);
    }).catch(() => toast.error("No se pudieron cargar las columnas"));
  }, [open, idsSeleccionadas.length]);

  const n = alcance === "filtradas" ? idsFiltradas.length : alcance === "seleccionadas" ? idsSeleccionadas.length : totalCargadas;
  const hojasSel = Object.keys(hojas).filter((h) => hojas[h]);
  const sinCols = (hojas.peticiones && !(colsP || []).length) || (hojas.lineas && !(colsL || []).length) || (hojas.componentes && !(colsC || []).length);
  const invalido = !meta || hojasSel.length === 0 || sinCols || (alcance !== "todas" && n === 0);
  const motivo = !meta ? "Cargando…" : hojasSel.length === 0 ? "Selecciona al menos una hoja" : sinCols ? "Selecciona columnas en cada hoja activa" : (alcance !== "todas" && n === 0) ? "No hay peticiones en este alcance" : "";

  const exportar = async () => {
    setBusy(true);
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ hojas, colsP, colsL, colsC }));
      const body = { alcance: alcance === "todas" ? "todas" : "ids", ids: alcance === "filtradas" ? idsFiltradas : alcance === "seleccionadas" ? idsSeleccionadas : [], hojas: hojasSel,
        columnas_peticiones: hojas.peticiones ? colsP : null, columnas_lineas: hojas.lineas ? colsL : null, columnas_componentes: hojas.componentes ? colsC : null, filtros_descripcion: alcance === "todas" ? { Alcance: "Todas (sin filtros)" } : filtrosDescripcion };
      const res = await api.post("/peticiones/export.xlsx", body, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const nombre = (cd.match(/filename="?([^";]+)"?/) || [])[1] || `peticiones_DNF_${new Date().toISOString().slice(0, 10)}_${alcance}_${n}.xlsx`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = nombre; a.setAttribute("data-testid", "export-download-link"); document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      const np = res.headers["x-export-peticiones"], nl = res.headers["x-export-lineas"], nc = res.headers["x-export-componentes"], nf = res.headers["x-export-ids-no-encontrados"];
      toast.success(`Excel generado · ${np ?? n} peticiones${hojas.lineas ? ` · ${nl ?? "?"} líneas` : ""}${hojas.componentes ? ` · ${nc ?? "?"} componentes` : ""}${Number(nf) > 0 ? ` · ${nf} ids no encontrados` : ""}`);
      onClose();
    } catch (e) {
      let msg = e?.response?.data?.detail;
      if (e?.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail; } catch { /* noop */ } }
      toast.error(typeof msg === "string" ? msg : "Error generando el Excel", { duration: 8000 });
    } finally { setBusy(false); }
  };

  const Radio = ({ v, label, count, disabled, hint }) => (
    <label className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${alcance === v ? "border-amber-500/60 bg-amber-500/10 text-zinc-100" : "border-zinc-800 text-zinc-300"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
      <input type="radio" name="alcance" value={v} checked={alcance === v} disabled={disabled} onChange={() => setAlcance(v)} data-testid={`export-alcance-${v}`} className="accent-amber-500" />
      <span className="font-semibold">{label}</span><span className="ml-auto font-mono text-amber-300" data-testid={`export-alcance-${v}-count`}>{count}</span>
      {hint && <span className="text-[10px] text-zinc-500 w-full pl-6">{hint}</span>}
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-zinc-950 border-zinc-800 max-h-[90vh] overflow-y-auto" data-testid="export-peticiones-dialog">
        <DialogHeader><DialogTitle className="text-zinc-100 flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-emerald-400" /> Exportar peticiones a Excel</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-3">
            <Radio v="filtradas" label="Filtradas" count={idsFiltradas.length} hint="Exactamente lo que ves en el listado, mismo orden" />
            <Radio v="seleccionadas" label="Seleccionadas" count={idsSeleccionadas.length} disabled={idsSeleccionadas.length === 0} hint="Solo las marcadas con checkbox" />
            <Radio v="todas" label="Todas" count={totalCargadas} hint="Ignora filtros · todas las peticiones" />
          </div>
          {meta && (
            <>
              <ColumnasHoja titulo="Peticiones" testid="export-peticiones" activa={!!hojas.peticiones} onToggleHoja={(v) => setHojas({ ...hojas, peticiones: v })} defs={meta.peticiones} presets={meta.presets.peticiones} sel={colsP || []} onChange={setColsP} />
              <ColumnasHoja titulo="Líneas / Trabajos" testid="export-lineas" activa={!!hojas.lineas} onToggleHoja={(v) => setHojas({ ...hojas, lineas: v })} defs={meta.lineas} presets={meta.presets.lineas} sel={colsL || []} onChange={setColsL} />
              <ColumnasHoja titulo="Componentes" testid="export-componentes" activa={!!hojas.componentes} onToggleHoja={(v) => setHojas({ ...hojas, componentes: v })} defs={meta.componentes} presets={meta.presets.componentes} sel={colsC || []} onChange={setColsC} />
              <p className="text-[11px] text-zinc-500">Las hojas Líneas / Trabajos y Componentes incluyen TODAS las líneas y componentes de cada petición exportada (aunque el buscador profundo haya coincidido solo con una línea). Fechas e importes se exportan como valores nativos de Excel.</p>
            </>
          )}
        </div>
        <DialogFooter className="gap-2 items-center">
          {invalido && <span className="text-xs text-amber-300 mr-auto" data-testid="export-motivo-bloqueo">{motivo}</span>}
          <Button variant="ghost" onClick={onClose} data-testid="export-cancelar">Cancelar</Button>
          <Button disabled={invalido || busy} onClick={exportar} data-testid="export-descargar" className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" /> Descargar .xlsx ({alcance === "todas" ? "todas" : n})</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
